import React, { useEffect, useRef, useState } from "react";
import { TabType, FuelType, HoverElement, SolveMode } from "../../types";
import { GridStateWithSolution, HourlyDispatchResult } from "../../powerFlow/multiTimestepOptimization";
import GeneratorTable from "./GeneratorTable";
import LoadTable from "./LoadTable";
import BusTable from "./BusTable";
import BatteryTable from "./BatteryTable";
import LineTable from "./LineTable";
import TimestepTable from "./TimestepTable";
import DemandTable from "./DemandTable";
import ObjectiveOverview from "./ObjectiveOverview";
import ObjectiveTableView from "./ObjectiveTableView";
import VariableTableView from "./VariableTableView";
import ConstraintTableView from "./ConstraintTableView";
import OptimizationGraph from "./OptimizationGraph";
import ConvergenceChart from "./ConvergenceChart";
import ResidualGraph from "./ResidualGraph";
import JacobianView from "./JacobianView";
import TimeStepsControl from "./TimeStepsControl";
import PanelTree, { TreeNode } from "./PanelTree";
import PanelTabs, { OpenTab } from "./PanelTabs";
import PanelBreadcrumb from "./PanelBreadcrumb";
import RuleEditor from "./RuleEditor";
import RelationTable from "./RelationTable";
import { HighlightedColumn } from "./ColumnHeader";
import { MenuItem } from "../common/Menu";
import MobileHeader from "../mobile/MobileHeader";
import MobileListRow from "../mobile/MobileListRow";
import { GroundedProblem, GroundedResidualSystem } from "../../powerFlow/groundedProblem";
import {
  Data,
  Catalog,
  CompiledRule,
  CompiledCost,
  CompiledRuleSet,
  draftDiagnostics,
  objectiveOf,
  objectiveToString,
  OBJECTIVE_NAME,
} from "../../powerFlow/language";

export interface Props {
  gridState: GridStateWithSolution;
  // Which solve produced this problem. In ACPF the objective/costs and the
  // optimization graph don't apply, and the rules aren't editable yet, so those
  // sections are hidden and the problem section lists only grounded groups.
  mode?: SolveMode;
  // The solved problem in backend-neutral form — variables, constraints, and
  // (LP only) objective cost categories, joined with their solution values. Both
  // the DCOPF LP and the ACPF Newton solve produce one, so the problem section
  // renders the same way in either mode.
  problem?: GroundedProblem;
  // The grounded residual system (ACPF only), for the residual-graph panel.
  residualSystem?: GroundedResidualSystem;
  // The base data catalog the rules are checked against, for live diagnostics
  // while editing — the AC base catalog in ACPF; DCOPF uses the default.
  baseCatalog?: Catalog;
  // The model's input relations (demand, gen_cap, …), derived from the grid and
  // curves. The DATA-section tables render these directly — they're inputs to
  // the optimization, not read back out of the solved model.
  inputData?: Data;
  // The compiled rule set: the symbolic problem the model is built from, with
  // each rule's text and any errors. The tree lists these (not just the groups
  // that happen to have grounded rows), so a disabled or broken rule stays
  // reachable and editable.
  rules: CompiledRuleSet;
  // Keyed by entry id, not name: a rule names itself in its own text, so an
  // edit can rename it and the name can't be what an edit is addressed to.
  onRuleSourceChange: (id: string, source: string) => void;
  onRuleEnabledChange: (id: string, enabled: boolean) => void;
  onRuleAdd: () => void;
  onRuleRemove: (id: string) => void;
  onCostSourceChange: (id: string, source: string) => void;
  onCostEnabledChange: (id: string, enabled: boolean) => void;
  onCostAdd: () => void;
  onCostRemove: (id: string) => void;
  onRulesReset: () => void;
  batteryStates?: Map<string, { soc: number; power: number }>;
  hourlyGridStates: HourlyDispatchResult[];
  onNodeCostChange?: (nodeId: string, cost: number) => void;
  onNodeFuelTypeChange?: (nodeId: string, fuelType: FuelType) => void;
  onBatteryParametersChange?: (
    batteryId: string,
    params: {
      maxChargeMW?: number;
      maxDischargeMW?: number;
      maxEnergyMWh?: number;
      minEnergyMWh?: number;
      efficiency?: number;
      initialEnergyMWh?: number;
    }
  ) => void;
  requireMatchingSOC?: boolean;
  onRequireMatchingSOCChange?: (value: boolean) => void;
  onEdgeRatingChange: (edgeId: string, rating: number) => void;
  // Commit the current live topology edit to the undo history as one step,
  // called when an input finishes changing (blur / drag release).
  onCommitEdit?: () => void;
  activeTab?: TabType;
  onTabChange?: (tab: TabType) => void;
  // Bumped on every "reveal in elements panel" request so the target tab is
  // opened/focused even when activeTab is unchanged.
  revealSeq?: number;
  hoverElement: HoverElement | null;
  onElementHover: (element: HoverElement | null) => void;
  hoverHour?: number | null;
  onHourHover?: (hour: number | null) => void;
  numTimeSteps: number;
  onNumTimeStepsChange: (value: number) => void;
  selectedBusId?: string | null;
  onBusSelect?: (nodeId: string) => void;
  // When true, render a touch-friendly drill-down (one screen per tree level)
  // instead of the desktop tree + tabs. onExit is called when the user backs
  // out of the top level (returns to the mobile home screen).
  mobile?: boolean;
  onExit?: () => void;
}

const VARIABLES_PREFIX = "variables:";
const CONSTRAINTS_PREFIX = "constraints:";
const COSTS_PREFIX = "costs:";
const DERIVED_PREFIX = "derived:";

// A panel's label is its id — which for a data panel is the relation's name, so
// the tree and the rules say the same word. Only the few panels that aren't
// relations need spelling out.
const FIXED_LABELS: Record<string, string> = {
  graph: "optimization graph",
  residualGraph: "residual graph",
};

// Icons let users tell categories apart at a glance — in particular every
// variable class shares 🔷, every constraint class 🔶, and every cost class 💰.
const VARIABLE_ICON = "🔷";
const CONSTRAINT_ICON = "🔶";
const COST_ICON = "💰";
const DERIVED_ICON = "🧮";
const ICONS: Record<string, string> = {
  gen: "⚡",
  loads: "💡",
  bus: "🔌",
  battery: "🔋",
  line: "↔️",
  timestep: "⏱️",
  soc_time: "⏳",
  demand: "📉",
  wind_strength: "🌬️",
  solar_strength: "☀️",
  gen_cap: "📈",
  horizon: "🏁",
  derived: DERIVED_ICON,
  objective: "🎯",
  graph: "🕸️",
  residualGraph: "🕸️",
  convergence: "📉",
  jacobian: "🔢",
  variables: VARIABLE_ICON,
  constraints: CONSTRAINT_ICON,
  costs: COST_ICON,
};

const iconFor = (id: string): string => {
  if (id.startsWith(VARIABLES_PREFIX)) return VARIABLE_ICON;
  if (id.startsWith(CONSTRAINTS_PREFIX)) return CONSTRAINT_ICON;
  if (id.startsWith(COSTS_PREFIX)) return COST_ICON;
  if (id.startsWith(DERIVED_PREFIX)) return DERIVED_ICON;
  return ICONS[id] ?? "";
};

const emptyNote = (message: string) => (
  <div style={{ padding: "16px", color: "#666", fontStyle: "italic" }}>
    {message}
  </div>
);

/** A rule's editor stays visible even when there's no solved model to tabulate. */
const withEditor = (editor: React.ReactNode, body: React.ReactNode) => (
  <>
    {editor}
    {body}
  </>
);

const ElementTable: React.FC<Props> = ({
  gridState,
  mode = "dcopf",
  problem,
  residualSystem,
  baseCatalog,
  inputData,
  rules,
  onRuleSourceChange,
  onRuleEnabledChange,
  onRuleAdd,
  onRuleRemove,
  onCostSourceChange,
  onCostEnabledChange,
  onCostAdd,
  onCostRemove,
  onRulesReset,
  batteryStates,
  hourlyGridStates,
  onNodeCostChange,
  onNodeFuelTypeChange,
  onBatteryParametersChange,
  requireMatchingSOC,
  onRequireMatchingSOCChange,
  onEdgeRatingChange,
  onCommitEdit,
  activeTab: propActiveTab,
  onTabChange,
  revealSeq = 0,
  hoverElement,
  onElementHover,
  hoverHour,
  onHourHover,
  numTimeSteps,
  onNumTimeStepsChange,
  selectedBusId,
  onBusSelect,
  mobile = false,
  onExit,
}) => {
  const requestedTab: TabType = propActiveTab ?? "gen";

  // The tree/tab area owns which tabs are open and which is focused. activeTab
  // (from global state) is treated as an open/focus request, so it stays in
  // sync with the URL and drives "reveal in elements panel".
  // Start with all the "data" tabs open (plus the requested tab, if different).
  const [openTabs, setOpenTabs] = useState<TabType[]>(() => {
    const initial: TabType[] = ["gen", "loads", "bus", "battery", "line"];
    return initial.includes(requestedTab) ? initial : [...initial, requestedTab];
  });
  const [activePanel, setActivePanel] = useState<TabType | null>(requestedTab);

  // The column a jump from a rule arrived at, e.g. ⌘-clicking `from` in
  // `l.from`. Held next to the panel it belongs to, so it can't outlive it, and
  // dropped as soon as a panel is opened any other way.
  const [columnHighlight, setColumnHighlight] = useState<{ panel: TabType; column: string } | null>(null);

  // Mobile drill-down navigation state: the folder path currently open (root
  // is []) and, when a leaf table is open, which one. Unused on desktop.
  const [mobileStack, setMobileStack] = useState<string[]>([]);
  const [mobileLeaf, setMobileLeaf] = useState<TabType | null>(null);

  const openAndFocus = (id: TabType) => {
    setOpenTabs((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setActivePanel(id);
  };

  // Open + focus whenever the requested tab changes or a reveal is fired
  // (revealSeq bumps even when the tab id is unchanged). Idempotent, so it's
  // safe when the change originated from a click in here.
  //
  // A reveal — "reveal in elements panel", or adding a rule — is an explicit
  // "show me this", so on mobile it also drills straight into the panel. Only
  // an actual bump counts: the initial render must leave the drill-down at its
  // home screen rather than opening whatever tab the URL carried.
  const lastRevealSeq = useRef(revealSeq);
  useEffect(() => {
    openAndFocus(requestedTab);
    if (revealSeq !== lastRevealSeq.current) {
      lastRevealSeq.current = revealSeq;
      if (mobile) setMobileLeaf(requestedTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab, revealSeq]);

  const selectPanel = (id: TabType) => {
    setColumnHighlight(null);
    openAndFocus(id);
    onTabChange?.(id);
  };

  const closeTab = (id: TabType) => {
    setOpenTabs((prev) => {
      const idx = prev.indexOf(id);
      const next = prev.filter((t) => t !== id);
      if (id === activePanel) {
        // Focus the neighbour that slides into this slot (or the previous one).
        const neighbour = next[idx] ?? next[idx - 1] ?? null;
        setActivePanel(neighbour);
        if (neighbour) onTabChange?.(neighbour);
      }
      return next;
    });
  };

  const generators = gridState.nodes.filter((node) => node.type === "generator");
  const loads = gridState.nodes.filter((node) => node.type === "load");
  const buses = gridState.nodes.filter((node) => node.type === "bus");
  const batteries = gridState.nodes.filter((node) => node.type === "battery");
  const lines = gridState.edges;

  // The LP solves a single connected component (id 0). Kept only for the
  // LP-only panels (the optimization graph and the objective overview); every
  // table below comes from the backend-neutral grounded problem instead.
  const currentModel = gridState.models
    ? Array.from(gridState.models.values())[0]
    : undefined;
  const currentSolution = gridState.lpSolutions
    ? Array.from(gridState.lpSolutions.values())[0]
    : undefined;

  // ACPF has no objective and no editable rule text yet, so its problem section
  // hides the objective/costs/graph and lists only grounded groups.
  const isAc = (problem?.kind ?? (mode === "acpf" ? "ac" : "lp")) === "ac";

  // One table per variable/constraint/cost class, from the grounded-problem view
  // model — the same shapes in either mode. The tree turns each into a leaf.
  const variableTables = problem?.variables ?? [];
  const constraintTables = problem?.constraints ?? [];
  const costTables = problem?.objective ?? [];

  // The solved values as a name→value map — the point the Jacobian panel
  // evaluates its entries at.
  const solutionPoint = new Map<string, number>(
    variableTables.flatMap((t) => t.rows.map((r) => [r.name, r.value ?? 0] as [string, number])),
  );

  // The catalog the DATA section renders from — the compiled rules' own, which
  // already follows the mode (DC relations in DCOPF, AC relations in ACPF).
  const dataCatalog = rules.catalog;

  // The input relations, in catalog order. Read off the *compiled* catalog, so
  // the DATA section and the rules always agree on what exists and what it's
  // called (the decision relations live under `variables`, declared by rules).
  const dataRelations = Object.values(dataCatalog).filter((schema) => schema.kind === "data");

  // --- Rules -----------------------------------------------------------------
  // A rule's section follows its body: a ranged rule declares a variable class
  // (`declares` names the relation), everything else grounds into constraint
  // rows under its own name. A rule that doesn't compile has no readable body
  // yet, so it's listed under constraints until it does.
  const variableRules = rules.rules.filter((rule) => rule.declares);
  const derivedRules = rules.rules.filter((rule) => rule.defines);
  const constraintRules = rules.rules.filter((rule) => !rule.declares && !rule.defines);

  const ruleWithId = (id: string): CompiledRule | undefined =>
    rules.rules.find((rule) => rule.id === id);

  // --- Data relations: base vs derived ---------------------------------------
  // A relation is derived when a rule computes it; everything else in the
  // catalog is base — read off the grid and the curves before any rule runs.
  const derivedNames = new Set(derivedRules.map((rule) => rule.defines!));
  const baseRelations = dataRelations.filter((schema) => !derivedNames.has(schema.name));

  const relationLeaf = (name: string): TreeNode => ({
    kind: "leaf",
    id: name as TabType,
    label: name,
    icon: iconFor(name),
    count: inputData?.[name]?.length,
  });

  /**
   * The group whose rows belong to an entry. Grounded rows are grouped by
   * *name*, so an entry that didn't reach the model — it doesn't compile, or a
   * sibling already has its name — owns none of them, even when a rule of the
   * same name did ground.
   */
  const groundedGroup = (
    entry: { name: string; errors: string[] } | undefined,
    fallback: string
  ): string | undefined => {
    if (!entry) return fallback;
    return entry.errors.length === 0 ? entry.name : undefined;
  };
  const ruleDeclaring = (relation: string): CompiledRule | undefined =>
    rules.rules.find((rule) => rule.declares === relation);

  /**
   * The panel a relation lives in: the rule that computes it for a derived
   * relation, the class of variables for a decision one, its own table for
   * everything else. Undefined only for a name that isn't in the catalog.
   */
  const panelForRelation = (name: string): TabType | undefined => {
    const derived = derivedRules.find((rule) => rule.defines === name);
    if (derived) return `derived:${derived.id}`;
    const schema = dataCatalog[name];
    if (!schema) return undefined;
    return schema.kind === "decision" ? `variables:${name}` : (name as TabType);
  };

  /**
   * Go to a relation's panel — what the editor's go-to-definition does with a
   * relation named in a rule. Deliberately the same call a click in the tree
   * makes, so it opens the tab, focuses it and lands in the URL identically.
   *
   * A jump from a *column* (`from` in `l.from`) lands on the same panel and
   * points out which column it came from, since a relation's panel is a table
   * and the column is the part of it being asked about.
   */
  const openRelation = (name: string, column?: string): void => {
    const panel = panelForRelation(name);
    if (!panel) return;
    selectPanel(panel);
    if (column) setColumnHighlight({ panel, column });
    if (mobile) setMobileLeaf(panel);
  };

  /**
   * Leaves for one section: one per authored entry (a rule or a cost category),
   * plus any grounded group with no entry behind it — so nothing in the model is
   * ever unreachable — de-duplicated by panel id.
   */
  const ruleLeaves = <T extends { id: string; name: string; enabled: boolean; errors: string[] }>(
    prefix: string,
    icon: string,
    entries: T[],
    /** The panel id for an entry: its own id, or — for variables — the relation it declares. */
    keyOf: (entry: T) => string,
    groundedNames: string[],
    countOf: (entry: T | undefined, key: string) => number | undefined,
    /**
     * What the leaf is called. Constraints and costs go by the entry's name, so
     * renaming in the editor renames the leaf while its panel id stays put;
     * variables go by the class they hold, since that's what the panel lists.
     */
    labelOf: (entry: T | undefined, key: string) => string = (entry, key) => entry?.name ?? key
  ): TreeNode[] => {
    const seen = new Set<string>();
    const leaves: TreeNode[] = [];
    const push = (key: string, entry?: T) => {
      if (seen.has(key)) return;
      seen.add(key);
      leaves.push({
        kind: "leaf",
        id: `${prefix}${key}` as TabType,
        label: labelOf(entry, key),
        icon,
        count: countOf(entry, key),
        danger: (entry?.errors.length ?? 0) > 0,
        inactive: entry ? !entry.enabled : false,
      });
    };
    entries.forEach((entry) => push(keyOf(entry), entry));
    groundedNames.forEach((name) => push(name));
    return leaves;
  };

  // --- Rule editors ----------------------------------------------------------
  // Each rule panel opens with its rule; edits are applied on blur and flow back
  // up to the reducer, which re-grounds and re-solves.
  //
  // The editor is given the catalog it's checked against and a way to open a
  // relation's panel, so ⌘-clicking a relation in a rule goes where clicking it
  // in the tree would.
  const ruleEditorFor = (rule: CompiledRule | undefined): React.ReactNode =>
    rule && (
      <RuleEditor
        key={rule.id}
        source={rule.source}
        disabled={!rule.enabled}
        // Only `declares`, which names something the text doesn't say outright:
        // the variable class a ranged body bounds, read off its subject. A
        // definition's relation is its own name, already in the `def` head.
        label={rule.declares ? `declares ${rule.declares}` : undefined}
        catalog={rules.catalog}
        onNavigate={openRelation}
        diagnose={(draft) => draftDiagnostics(rules, "rules", rule.id, draft, baseCatalog)}
        onChange={(source) => onRuleSourceChange(rule.id, source)}
      />
    );

  const costWithId = (id: string): CompiledCost | undefined =>
    rules.costs.find((cost) => cost.id === id);

  // A cost category is a bare expression — one summand of `minimize cost = …` —
  // so its editor is labelled with the head it plugs into.
  const costEditorFor = (cost: CompiledCost | undefined): React.ReactNode =>
    cost && (
      <RuleEditor
        key={cost.id}
        source={cost.source}
        disabled={!cost.enabled}
        label={`one term of: minimize ${OBJECTIVE_NAME} = …`}
        catalog={rules.catalog}
        onNavigate={openRelation}
        diagnose={(draft) => draftDiagnostics(rules, "costs", cost.id, draft, baseCatalog)}
        onChange={(source) => onCostSourceChange(cost.id, source)}
      />
    );

  const treeNodes: TreeNode[] = [
    {
      kind: "folder",
      id: "data",
      label: "data",
      // Two kinds of relation, kept apart: the ones the grid and the curves hand
      // to the model, and the ones the model works out for itself from those.
      children: [
        {
          kind: "folder",
          id: "base",
          label: "base",
          // One leaf per input relation, from the same catalog the rules are
          // checked against — so what the tree calls a table is what a rule has
          // to call it. `loads` rides along after `bus`: load nodes are grid
          // state that feeds `demand`, not a relation of their own.
          children: baseRelations.flatMap((schema): TreeNode[] => {
            const leaf = relationLeaf(schema.name);
            return schema.name === "bus"
              ? [leaf, { kind: "leaf", id: "loads", label: "loads", icon: iconFor("loads"), count: loads.length }]
              : [leaf];
          }),
        },
        ...(derivedRules.length > 0
          ? [
              {
                kind: "folder" as const,
                id: "derived",
                label: "derived",
                // One leaf per definition rule. A rule defines exactly one
                // relation — cases inside it are a conditional, not a rule each
                // — so this is a list of relations as much as of rules, and it
                // opens the rule's text over the rows it produces.
                children: ruleLeaves(
                  DERIVED_PREFIX,
                  DERIVED_ICON,
                  derivedRules,
                  (rule) => rule.id,
                  [],
                  (rule) => (rule?.defines ? inputData?.[rule.defines]?.length : undefined)
                ),
              },
            ]
          : []),
      ],
    },
    {
      kind: "folder",
      id: "problem",
      label: "problem",
      children: [
        // The objective, its cost categories, and the LP coupling graph are
        // meaningless for AC power flow (feasibility, no objective), so they're
        // hidden in that mode.
        ...(isAc
          ? []
          : [
              {
                kind: "leaf" as const,
                id: "objective" as TabType,
                label: "objective",
                icon: iconFor("objective"),
                // Flag the objective in red when the model couldn't be solved.
                danger: gridState.solutionValid === false,
              },
              {
                kind: "folder" as const,
                id: "costs" as TabType,
                label: "costs",
                children: ruleLeaves(
                  COSTS_PREFIX,
                  COST_ICON,
                  rules.costs,
                  (cost) => cost.id,
                  costTables.map((t) => t.name),
                  (cost, key) =>
                    costTables.find((t) => t.name === groundedGroup(cost, key))?.rows.length ?? 0
                ),
              },
            ]),
        {
          kind: "folder",
          id: "constraints",
          label: isAc ? "residuals" : "constraints",
          count: problem?.counts.constraints,
          children: ruleLeaves(
            CONSTRAINTS_PREFIX,
            CONSTRAINT_ICON,
            constraintRules,
            (rule) => rule.id,
            constraintTables.map((t) => t.name),
            (rule, key) => constraintTables.find((t) => t.name === groundedGroup(rule, key))?.rows.length ?? 0
          ),
        },
        {
          kind: "folder",
          id: "variables",
          label: "variables",
          count: problem?.counts.variables,
          // Keyed by the relation declared, not by the rule: the variables are
          // grouped by class, and that's what the panel tabulates.
          children: ruleLeaves(
            VARIABLES_PREFIX,
            VARIABLE_ICON,
            variableRules,
            (rule) => rule.declares!,
            variableTables.map((t) => t.name),
            (_rule, key) => variableTables.find((t) => t.name === key)?.rows.length ?? 0,
            (_rule, key) => key
          ),
        },
        // AC power flow shows its Newton convergence and the residual expression
        // DAG; the LP shows its variable-coupling optimization graph.
        ...(isAc
          ? [
              {
                kind: "leaf" as const,
                id: "convergence" as TabType,
                label: "convergence",
                icon: iconFor("convergence"),
                danger: problem?.convergence?.converged === false,
              },
              {
                kind: "leaf" as const,
                id: "residualGraph" as TabType,
                label: "residual graph",
                icon: iconFor("residualGraph"),
              },
              {
                kind: "leaf" as const,
                id: "jacobian" as TabType,
                label: "jacobian",
                icon: iconFor("jacobian"),
              },
            ]
          : [{ kind: "leaf" as const, id: "graph" as TabType, label: "optimization graph", icon: iconFor("graph") }]),
      ],
    },
  ];

  // A panel id is stable; its label is the name the entry's text gives it, so a
  // rename shows up on the tab as well as in the tree.
  const labelFor = (id: TabType): string => {
    if (id.startsWith(VARIABLES_PREFIX)) return id.slice(VARIABLES_PREFIX.length);
    if (id.startsWith(CONSTRAINTS_PREFIX)) {
      const key = id.slice(CONSTRAINTS_PREFIX.length);
      return ruleWithId(key)?.name ?? key;
    }
    if (id.startsWith(DERIVED_PREFIX)) {
      const key = id.slice(DERIVED_PREFIX.length);
      return ruleWithId(key)?.name ?? key;
    }
    if (id.startsWith(COSTS_PREFIX)) {
      const key = id.slice(COSTS_PREFIX.length);
      return costWithId(key)?.name ?? key;
    }
    return FIXED_LABELS[id] ?? id;
  };

  const openTabItems: OpenTab[] = openTabs.map((id) => ({
    id,
    label: labelFor(id),
    icon: iconFor(id),
  }));

  // Path of labels from the tree root down to a panel, for the breadcrumb.
  const pathFor = (id: TabType): string[] => {
    const walk = (nodes: TreeNode[], trail: string[]): string[] | null => {
      for (const node of nodes) {
        const here = [...trail, node.label];
        if (node.kind === "leaf") {
          if (node.id === id) return here;
        } else {
          const found = walk(node.children, here);
          if (found) return found;
        }
      }
      return null;
    };
    return walk(treeNodes, []) ?? [labelFor(id)];
  };

  // --- Tree context menus ----------------------------------------------------
  // Right-clicking the constraints/variables folders adds a rule; right-clicking
  // a rule's leaf enables, disables or deletes it. Adding doesn't ask for a name
  // any more — the new rule's text names itself, and renaming it is an edit.
  const addRuleItem: MenuItem = { kind: "action", label: "Add rule", icon: "➕", onClick: onRuleAdd };
  const addCostItem: MenuItem = { kind: "action", label: "Add cost category", icon: "➕", onClick: onCostAdd };
  const resetItem: MenuItem = {
    kind: "action",
    label: "Reset rules and costs to defaults",
    icon: "↺",
    danger: true,
    onClick: () => {
      if (window.confirm("Discard every rule and cost edit and restore the built-in ones?")) {
        onRulesReset();
      }
    },
  };

  /**
   * The commands for one named, toggleable entry — a rule or a cost category.
   * The two differ only in what they act on, so the shape is written once.
   */
  const entryMenu = (
    entry: { id: string; name: string; enabled: boolean },
    noun: string,
    addSibling: MenuItem,
    setEnabled: (id: string, enabled: boolean) => void,
    remove: (id: string) => void
  ): MenuItem[] => [
    {
      kind: "action",
      label: entry.enabled ? `Disable ${entry.name}` : `Enable ${entry.name}`,
      // A checkmark reads as the entry's on/off state, the way a menu's
      // checkable item does — so the row shows what's true, not just what
      // clicking will do.
      checked: entry.enabled,
      onClick: () => setEnabled(entry.id, !entry.enabled),
    },
    addSibling,
    { kind: "separator" },
    {
      kind: "action",
      label: `Delete ${entry.name}`,
      icon: "🗑️",
      danger: true,
      onClick: () => {
        if (window.confirm(`Delete the ${noun} "${entry.name}"?`)) remove(entry.id);
      },
    },
  ];

  const menuFor = (node: TreeNode): MenuItem[] | undefined => {
    if (node.kind === "folder") {
      if (node.id === "costs") return [addCostItem, { kind: "separator" }, resetItem];
      if (node.id === "constraints" || node.id === "variables" || node.id === "derived") {
        return [addRuleItem, { kind: "separator" }, resetItem];
      }
      // "problem" holds all three sections, so it offers both kinds.
      if (node.id === "problem") return [addRuleItem, addCostItem, { kind: "separator" }, resetItem];
      return undefined;
    }

    if (node.id.startsWith(DERIVED_PREFIX)) {
      const rule = ruleWithId(node.id.slice(DERIVED_PREFIX.length));
      if (!rule) return [addRuleItem];
      return entryMenu(rule, "rule", addRuleItem, onRuleEnabledChange, onRuleRemove);
    }

    if (node.id.startsWith(COSTS_PREFIX)) {
      const cost = costWithId(node.id.slice(COSTS_PREFIX.length));
      // A grounded group with no category behind it has nothing to act on.
      if (!cost) return [addCostItem];
      return entryMenu(cost, "cost category", addCostItem, onCostEnabledChange, onCostRemove);
    }

    const isRulePanel =
      node.id.startsWith(CONSTRAINTS_PREFIX) || node.id.startsWith(VARIABLES_PREFIX);
    if (!isRulePanel) return undefined;

    const key = node.id.startsWith(CONSTRAINTS_PREFIX)
      ? node.id.slice(CONSTRAINTS_PREFIX.length)
      : node.id.slice(VARIABLES_PREFIX.length);
    const rule = node.id.startsWith(CONSTRAINTS_PREFIX) ? ruleWithId(key) : ruleDeclaring(key);
    if (!rule) return [addRuleItem];

    return entryMenu(rule, "rule", addRuleItem, onRuleEnabledChange, onRuleRemove);
  };

  const isTablePanel = (id: TabType | null): boolean =>
    !!id &&
    (id.startsWith(VARIABLES_PREFIX) ||
      id.startsWith(CONSTRAINTS_PREFIX) ||
      id.startsWith(COSTS_PREFIX) ||
      id.startsWith(DERIVED_PREFIX));

  const noSolver = "No solver information available. Run a power flow calculation to see LP problem and solution details.";

  /**
   * A panel, with the column a jump arrived at in scope for its table. Scoped to
   * the panel that jump was for: another tab showing the same relation's rows
   * shouldn't light up because of it.
   */
  const renderPanelHighlighted = (id: TabType) => (
    <HighlightedColumn.Provider value={columnHighlight?.panel === id ? columnHighlight.column : undefined}>
      {renderPanel(id)}
    </HighlightedColumn.Provider>
  );

  const renderPanel = (id: TabType) => {
    if (id.startsWith(VARIABLES_PREFIX)) {
      const group = id.slice(VARIABLES_PREFIX.length);
      const editor = ruleEditorFor(ruleDeclaring(group));
      if (!problem) return withEditor(editor, emptyNote(noSolver));
      return (
        <VariableTableView
          table={variableTables.find((t) => t.name === group)}
          ruleEditor={editor}
        />
      );
    }
    if (id.startsWith(CONSTRAINTS_PREFIX)) {
      const key = id.slice(CONSTRAINTS_PREFIX.length);
      const rule = ruleWithId(key);
      const editor = ruleEditorFor(rule);
      if (!problem) return withEditor(editor, emptyNote(noSolver));
      return (
        <ConstraintTableView
          table={constraintTables.find((t) => t.name === groundedGroup(rule, key))}
          ruleEditor={editor}
        />
      );
    }
    if (id.startsWith(DERIVED_PREFIX)) {
      const rule = ruleWithId(id.slice(DERIVED_PREFIX.length));
      const editor = ruleEditorFor(rule);
      const schema = rule?.defines ? rules.catalog[rule.defines] : undefined;
      if (!schema) return withEditor(editor, emptyNote("This rule defines no relation yet."));
      return withEditor(
        editor,
        <RelationTable schema={schema} rows={inputData?.[schema.name]} catalog={rules.catalog} />,
      );
    }
    if (id.startsWith(COSTS_PREFIX)) {
      const key = id.slice(COSTS_PREFIX.length);
      const cost = costWithId(key);
      const editor = costEditorFor(cost);
      const table = problem && costTables.find((t) => t.name === groundedGroup(cost, key));
      return table ? (
        <ObjectiveTableView table={table} ruleEditor={editor} />
      ) : (
        withEditor(editor, emptyNote(problem ? "No cost terms available" : noSolver))
      );
    }

    // ACPF problem panels, and its DATA relations — the latter render generically
    // rather than through the DC-specific hand-built tables (BusTable, etc.).
    if (id === "convergence") {
      return problem?.convergence ? (
        <ConvergenceChart convergence={problem.convergence} />
      ) : (
        emptyNote(noSolver)
      );
    }
    if (id === "residualGraph") {
      return <ResidualGraph system={residualSystem} />;
    }
    if (id === "jacobian") {
      return <JacobianView system={residualSystem} point={solutionPoint} />;
    }
    if (isAc && dataCatalog[id]?.kind === "data") {
      return (
        <RelationTable schema={dataCatalog[id]} rows={inputData?.[id]} catalog={dataCatalog} />
      );
    }

    switch (id) {
      case "gen":
        return (
          <GeneratorTable
            generators={generators}
            onNodeCostChange={onNodeCostChange}
            onNodeFuelTypeChange={onNodeFuelTypeChange}
            onCommitEdit={onCommitEdit}
            hoverElement={hoverElement}
            onElementHover={onElementHover}
            selectedBusId={selectedBusId}
            onBusSelect={onBusSelect}
          />
        );
      case "loads":
        return (
          <LoadTable
            loads={loads}
            hoverElement={hoverElement}
            onElementHover={onElementHover}
            selectedBusId={selectedBusId}
            onBusSelect={onBusSelect}
          />
        );
      case "bus":
        return (
          <BusTable
            buses={buses}
            lines={lines}
            hoverElement={hoverElement}
            onElementHover={onElementHover}
            selectedBusId={selectedBusId}
            onBusSelect={onBusSelect}
          />
        );
      case "battery":
        return (
          <BatteryTable
            batteries={batteries}
            batteryStates={batteryStates}
            hourlyGridStates={hourlyGridStates}
            onBatteryParametersChange={onBatteryParametersChange}
            onCommitEdit={onCommitEdit}
            requireMatchingSOC={requireMatchingSOC}
            onRequireMatchingSOCChange={onRequireMatchingSOCChange}
            hoverElement={hoverElement}
            onElementHover={onElementHover}
            hoverHour={hoverHour}
            onHourHover={onHourHover}
            numTimeSteps={numTimeSteps}
          />
        );
      case "line":
        return (
          <LineTable
            lines={lines}
            nodes={gridState.nodes}
            onEdgeRatingChange={onEdgeRatingChange}
            onCommitEdit={onCommitEdit}
            hoverElement={hoverElement}
            onElementHover={onElementHover}
          />
        );
      case "timestep":
        return <TimestepTable numTimeSteps={numTimeSteps} />;
      case "demand":
        return <DemandTable demand={inputData?.demand} />;
      case "objective":
        return (
          <ObjectiveOverview
            model={currentModel}
            solution={currentSolution}
            solutionValid={gridState.solutionValid}
            solutionMessage={gridState.solutionMessage}
            // Read-only: `minimize cost` is fixed, and what it sums is the
            // enabled cost categories — each edited in its own panel.
            objectiveText={objectiveToString(objectiveOf(rules.program))}
          />
        );
      case "graph":
        return currentModel ? (
          <OptimizationGraph model={currentModel} selectedComponent={0} />
        ) : (
          emptyNote(noSolver)
        );
    }

    // Every other data relation — the ones with no hand-built table of their
    // own — renders straight from its schema and its rows.
    const schema = dataCatalog[id];
    if (schema?.kind === "data") {
      return <RelationTable schema={schema} rows={inputData?.[id]} catalog={dataCatalog} />;
    }
    return null;
  };

  // --- Mobile drill-down -----------------------------------------------------
  // Reuses the same treeNodes and renderPanel as the desktop tree, but shows
  // one level per screen: root folders → nested folders → leaf tables.
  if (mobile) {
    // Walk the open folder path once: the folders themselves (for the header's
    // labels and its menu) and the children the current screen lists.
    const openFolders = (): Extract<TreeNode, { kind: "folder" }>[] => {
      const chain: Extract<TreeNode, { kind: "folder" }>[] = [];
      let nodes: TreeNode[] = treeNodes;
      for (const id of mobileStack) {
        const folder = nodes.find(
          (n): n is Extract<TreeNode, { kind: "folder" }> =>
            n.kind === "folder" && n.id === id
        );
        if (!folder) break;
        chain.push(folder);
        nodes = folder.children;
      }
      return chain;
    };

    const back = () => {
      if (mobileLeaf) {
        setMobileLeaf(null);
      } else if (mobileStack.length > 0) {
        setMobileStack((prev) => prev.slice(0, -1));
      } else {
        onExit?.();
      }
    };

    // A leaf table is open: show it full-screen with a breadcrumb subtitle.
    if (mobileLeaf) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "white",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MobileHeader
            title={labelFor(mobileLeaf)}
            onBack={back}
            subtitle={pathFor(mobileLeaf).join(" / ")}
            // The same commands the list row offers, so a rule can be disabled
            // or deleted from the screen its editor is on.
            menuItems={menuFor({
              kind: "leaf",
              id: mobileLeaf,
              label: labelFor(mobileLeaf),
            })}
          />
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              padding: isTablePanel(mobileLeaf) ? 0 : "16px",
            }}
          >
            {renderPanelHighlighted(mobileLeaf)}
          </div>
        </div>
      );
    }

    const chain = openFolders();
    const currentFolder = chain[chain.length - 1];
    const labels = chain.map((folder) => folder.label);
    const atRoot = mobileStack.length === 0;
    // Everything below the open folder — or the top-level folders at the root.
    const children = currentFolder ? currentFolder.children : mobileStack.length ? [] : treeNodes;

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          background: "white",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <MobileHeader
          title={atRoot ? "Elements" : labels[labels.length - 1]}
          onBack={back}
          subtitle={atRoot ? undefined : ["Elements", ...labels.slice(0, -1)].join(" / ")}
          // The open folder's own commands ("Add rule…"): its row is a screen
          // back, so without this there'd be no way to add a rule from inside
          // the constraints list.
          menuItems={currentFolder && menuFor(currentFolder)}
        />
        {/* Time-step control lives at the elements root since it drives every
            problem table. */}
        {atRoot && (
          <TimeStepsControl
            numTimeSteps={numTimeSteps}
            onNumTimeStepsChange={onNumTimeStepsChange}
          />
        )}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", background: "#f5f5f5" }}>
          {children.map((node) =>
            node.kind === "folder" ? (
              <MobileListRow
                key={node.id}
                icon={iconFor(node.id)}
                label={node.label}
                count={node.count}
                menuItems={menuFor(node)}
                drillsDown
                onClick={() => setMobileStack((prev) => [...prev, node.id])}
              />
            ) : (
              <MobileListRow
                key={node.id}
                icon={node.icon}
                label={node.label}
                count={node.count}
                danger={node.danger}
                inactive={node.inactive}
                menuItems={menuFor(node)}
                drillsDown
                onClick={() => {
                  selectPanel(node.id);
                  setMobileLeaf(node.id);
                }}
              />
            )
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: "white",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Time steps control */}
      <TimeStepsControl
        numTimeSteps={numTimeSteps}
        onNumTimeStepsChange={onNumTimeStepsChange}
      />

      {/* Tree (left) + tabbed panel area (right) */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <PanelTree
          nodes={treeNodes}
          activePanel={activePanel}
          onSelect={selectPanel}
          menuFor={menuFor}
        />

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <PanelTabs
            tabs={openTabItems}
            activePanel={activePanel}
            onSelect={selectPanel}
            onClose={closeTab}
          />
          {activePanel && <PanelBreadcrumb path={pathFor(activePanel)} />}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              // Table panels go edge-to-edge (with sticky headers); other panels
              // keep a comfortable margin.
              padding: isTablePanel(activePanel) ? 0 : "16px",
            }}
          >
            {activePanel
              ? renderPanelHighlighted(activePanel)
              : emptyNote("Select an item from the tree to open it.")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ElementTable;
